package com.havn.app.features.properties.data

import androidx.paging.Pager
import androidx.paging.PagingConfig
import androidx.paging.PagingData
import androidx.paging.PagingSource
import androidx.paging.PagingState
import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.media.HavnMediaBucket
import com.havn.app.core.media.havnDisplayPhotoSource
import com.havn.app.core.media.decodeHavnDataUrlBytes
import com.havn.app.core.media.havnFileExtension
import com.havn.app.core.media.havnDataUrlContentType
import com.havn.app.core.media.havnThumbnailOnlyPhotoSource
import com.havn.app.features.properties.domain.Property
import com.havn.app.features.properties.domain.PropertyDraft
import com.havn.app.features.properties.domain.PropertyPerformanceStats
import com.havn.app.features.properties.domain.PropertyPhoto
import com.havn.app.features.properties.domain.PropertyPhotoUpload
import com.havn.app.features.properties.domain.PropertyRepository
import com.havn.app.features.properties.domain.PropertyStatus
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.storage.storage
import io.ktor.http.ContentType
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Clock

class SupabasePropertyRepository(
    private val client: SupabaseClient
) : PropertyRepository {
    private val pageCacheMutex = Mutex()
    private val pageCache = mutableMapOf<SearchPageCacheKey, PropertyPageCacheEntry>()

    override suspend fun ensureAgentProfile(agentId: String, email: String?): Result<Unit> {
        return Result.success(Unit)
    }

    override suspend fun propertiesForAgent(agentId: String, agencyId: String?, actAsAgent: Boolean): Result<List<Property>> {
        return propertyPage(agentId, agencyId, query = "", offset = 0, limit = PROPERTY_PAGE_SIZE, actAsAgent = actAsAgent)
    }

    override suspend fun propertyPage(
        agentId: String,
        agencyId: String?,
        query: String,
        offset: Int,
        limit: Int,
        actAsAgent: Boolean
    ): Result<List<Property>> {
        val normalizedQuery = query.trim()
        val cacheKey = SearchPageCacheKey(agentId, agencyId, actAsAgent, normalizedQuery, offset, limit)
        return pageCacheMutex.withLock {
            val nowMs = Clock.System.now().toEpochMilliseconds()
            pageCache[cacheKey]
                ?.takeIf { nowMs - it.savedAtMs <= PAGE_CACHE_TTL_MS }
                ?.let { return@withLock Result.success(it.value) }

            val result = runCatching {
                HavnLogger.debug("Properties", "Loading properties for agent $agentId in agency ${agencyId ?: "personal"}")
                val rows = propertySearchRows(agencyId, normalizedQuery, offset, limit, actAsAgent)
                    .decodeList<PropertyRow>()

                val propertyIds = rows.map { it.id }
                val statsByProperty = statsForProperties(propertyIds)
                HavnLogger.debug("Properties", "Loaded ${rows.size} property rows for agent $agentId")

                rows.map { row ->
                    row.toDomain(
                        stats = statsByProperty[row.id] ?: PropertyPerformanceStats()
                    )
                }.sortedByLastModified()
            }.onFailure {
                HavnLogger.error("Properties", "Failed to load properties for agent $agentId", it)
            }
            result.onSuccess { pageCache[cacheKey] = PropertyPageCacheEntry(nowMs, it) }
            result
        }
    }

    override fun pagedPropertiesForAgent(agentId: String, agencyId: String?): Flow<PagingData<Property>> {
        return Pager(
            config = PagingConfig(
                pageSize = PROPERTY_PAGE_SIZE,
                initialLoadSize = PROPERTY_PAGE_SIZE,
                prefetchDistance = 4,
                enablePlaceholders = false
            ),
            pagingSourceFactory = {
                PropertyPagingSource(
                    repository = this,
                    agentId = agentId,
                    agencyId = agencyId
                )
            }
        ).flow
    }

    override suspend fun propertyDetail(agentId: String, propertyId: String): Result<Property> {
        return runCatching {
            HavnLogger.debug("Properties", "Loading property detail $propertyId")
            val row = client.from(PROPERTIES)
                .select {
                    filter {
                        eq("id", propertyId)
                    }
                    limit(1)
                }
                .decodeList<PropertyRow>()
                .firstOrNull()
                ?: propertyDetailFallbackRow(propertyId)

            val photos = photosForProperty(propertyId)
            val stats = statsForProperty(propertyId)
            if (photos.isEmpty()) {
                row.toDomain(stats = stats)
            } else {
                row.toDomain(photos = photos, stats = stats)
            }
        }.onFailure {
            HavnLogger.error("Properties", "Failed to load property detail $propertyId", it)
        }
    }

    private suspend fun propertyDetailFallbackRow(propertyId: String): PropertyRow {
        HavnLogger.warning("Properties", "Falling back to property search RPC for detail $propertyId")
        return propertySearchRows(agencyId = null, query = "", offset = 0, limit = PROPERTY_DETAIL_FALLBACK_LIMIT)
            .decodeList<PropertyRow>()
            .firstOrNull { it.id == propertyId }
            ?: error("Property is no longer available.")
    }

    override suspend fun propertyAssignmentAgentIds(propertyId: String): Result<List<String>> {
        return runCatching {
            client.from(PROPERTY_ASSIGNMENTS)
                .select(Columns.list("agent_id")) {
                    filter { eq("property_id", propertyId) }
                    order("created_at", Order.ASCENDING)
                }
                .decodeList<PropertyAssignmentRow>()
                .map { it.agentId }
        }.onFailure {
            HavnLogger.error("Properties", "Failed to load property assignments for $propertyId", it)
        }
    }

    override suspend fun addProperty(agentId: String, draft: PropertyDraft, agencyId: String?): Result<Property> {
        return runCatching {
            HavnLogger.info("Properties", "Adding property '${draft.title.trim()}' for agent $agentId")
            val row = client.from(PROPERTIES)
                .insert(draft.toInsertRow(agentId, agencyId)) {
                    select()
                }
                .decodeSingle<PropertyRow>()

            clearPageCache()
            row.toDomain()
        }.onFailure {
            HavnLogger.error("Properties", "Failed to add property '${draft.title.trim()}'", it)
        }
    }

    override suspend fun updateProperty(
        agentId: String,
        propertyId: String,
        draft: PropertyDraft
    ): Result<Property> {
        return runCatching {
            HavnLogger.info("Properties", "Updating property $propertyId")
            val row = client.from(PROPERTIES)
                .update(draft.toUpdateRow()) {
                    filter {
                        eq("id", propertyId)
                    }
                    select()
                }
                .decodeSingle<PropertyRow>()

            clearPageCache()
            row.toDomain(
                photos = photosForProperty(propertyId),
                stats = statsForProperty(propertyId)
            )
        }.onFailure {
            HavnLogger.error("Properties", "Failed to update property $propertyId", it)
        }
    }

    override suspend fun assignPropertyAccess(
        propertyId: String,
        agentIds: List<String>
    ): Result<Unit> {
        return runCatching {
            HavnLogger.info("Properties", "Assigning property $propertyId to ${agentIds.size} team members")
            client.postgrest.rpc(
                function = "assign_property_access",
                parameters = AssignPropertyAccessParams(
                    propertyId = propertyId,
                    agentIds = agentIds.distinct()
                )
            )
            clearPageCache()
            Unit
        }.onFailure {
            HavnLogger.error("Properties", "Failed to assign property access for $propertyId", it)
        }
    }

    override suspend fun archiveProperty(agentId: String, propertyId: String): Result<Unit> {
        return runCatching {
            HavnLogger.info("Properties", "Archiving property $propertyId")
            client.from(PROPERTIES).update(
                PropertyArchiveRow(status = PropertyStatus.Archived.databaseValue)
            ) {
                filter {
                    eq("id", propertyId)
                }
            }
            clearPageCache()
            Unit
        }.onFailure {
            HavnLogger.error("Properties", "Failed to archive property $propertyId", it)
        }
    }

    override suspend fun deleteProperty(agentId: String, propertyId: String): Result<Unit> {
        return runCatching {
            HavnLogger.warning("Properties", "Deleting property $propertyId")
            client.from(PROPERTIES).delete {
                filter {
                    eq("id", propertyId)
                }
            }
            clearPageCache()
            Unit
        }.onFailure {
            HavnLogger.error("Properties", "Failed to delete property $propertyId", it)
        }
    }

    override suspend fun uploadPropertyPhoto(
        agentId: String,
        propertyId: String,
        upload: PropertyPhotoUpload
    ): Result<PropertyPhoto> {
        return runCatching {
            require(upload.bytes.isNotEmpty()) { "Choose a non-empty photo before uploading." }
            val thumbnailBytes = upload.thumbnailDataUrl?.decodeHavnDataUrlBytes()
            val listThumbnailBytes = upload.listThumbnailDataUrl?.decodeHavnDataUrlBytes()
            val safeName = upload.fileName.sanitizeFileName()
            val uploadSlug = timestampSlug()
            val storagePath = "$agentId/properties/$propertyId/original/$uploadSlug-$safeName"
            HavnLogger.info("Properties", "Uploading property photo to $storagePath")

            client.storage.from(PROPERTY_PHOTOS_BUCKET).upload(storagePath, upload.bytes) {
                upsert = false
                contentType = ContentType.parse(upload.contentType)
            }
            val thumbnailContentType = upload.thumbnailDataUrl?.havnDataUrlContentType(upload.contentType) ?: upload.contentType
            val thumbnailStoragePath = thumbnailBytes?.let { bytes ->
                "$agentId/properties/$propertyId/thumbnails/$uploadSlug-thumbnail.${thumbnailContentType.havnFileExtension()}"
                    .also { path ->
                        client.storage.from(PROPERTY_PHOTOS_BUCKET).upload(path, bytes) {
                            upsert = false
                            contentType = ContentType.parse(thumbnailContentType)
                        }
                    }
            }
            val listThumbnailContentType = upload.listThumbnailDataUrl?.havnDataUrlContentType(thumbnailContentType) ?: thumbnailContentType
            val listThumbnailStoragePath = listThumbnailBytes?.let { bytes ->
                "$agentId/properties/$propertyId/list-thumbnails/$uploadSlug-list.${listThumbnailContentType.havnFileExtension()}"
                    .also { path ->
                        client.storage.from(PROPERTY_PHOTOS_BUCKET).upload(path, bytes) {
                            upsert = false
                            contentType = ContentType.parse(listThumbnailContentType)
                        }
                    }
            } ?: thumbnailStoragePath
            val existingPhotos = photosForProperty(propertyId)
            val shouldBeHero = upload.isHero || existingPhotos.isEmpty()
            val photo = try {
                client.from(PHOTOS)
                    .insert(
                        PropertyPhotoInsertRow(
                            propertyId = propertyId,
                            uploadedByAgentId = agentId,
                            storagePath = storagePath,
                            altText = upload.altText,
                            sortOrder = existingPhotos.size,
                            isHero = shouldBeHero,
                            thumbnailDataUrl = thumbnailStoragePath,
                            thumbnailStoragePath = thumbnailStoragePath,
                            listThumbnailDataUrl = listThumbnailStoragePath,
                            listThumbnailStoragePath = listThumbnailStoragePath,
                            thumbnailWidth = upload.thumbnailWidth,
                            thumbnailHeight = upload.thumbnailHeight,
                            contentType = upload.contentType,
                            sizeBytes = upload.bytes.size.toLong()
                        )
                    ) {
                        select()
                    }
                    .decodeSingle<PropertyPhotoRow>()
            } catch (error: Throwable) {
                runCatching { client.storage.from(PROPERTY_PHOTOS_BUCKET).delete(storagePath) }
                thumbnailStoragePath?.let { path ->
                    runCatching { client.storage.from(PROPERTY_PHOTOS_BUCKET).delete(path) }
                }
                listThumbnailStoragePath
                    ?.takeUnless { it == thumbnailStoragePath }
                    ?.let { path ->
                        runCatching { client.storage.from(PROPERTY_PHOTOS_BUCKET).delete(path) }
                    }
                throw error
            }

            if (shouldBeHero) {
                client.from(PROPERTIES).update(PropertyHeroPhotoRow(heroPhotoPath = storagePath)) {
                    filter {
                        eq("id", propertyId)
                    }
                }
            }

            clearPageCache()
            photo.toDomain()
        }.onFailure {
            HavnLogger.error("Properties", "Failed to upload property photo for $propertyId", it)
        }
    }

    override suspend fun deletePropertyPhoto(
        agentId: String,
        propertyId: String,
        photoId: String
    ): Result<Unit> {
        return runCatching {
            HavnLogger.warning("Properties", "Deleting property photo $photoId for $propertyId")
            val photo = client.from(PHOTOS)
                .select(Columns.list("id", "property_id", "storage_path", "thumbnail_storage_path", "list_thumbnail_storage_path", "is_hero")) {
                    filter {
                        eq("id", photoId)
                        eq("property_id", propertyId)
                    }
                    limit(1)
                }
                .decodeSingle<PropertyPhotoDeleteRow>()

            listOfNotNull(
                photo.storagePath,
                photo.thumbnailStoragePath,
                photo.listThumbnailStoragePath
            ).distinct().forEach { path ->
                runCatching { client.storage.from(PROPERTY_PHOTOS_BUCKET).delete(path) }
            }
            client.from(PHOTOS).delete {
                filter {
                    eq("id", photoId)
                    eq("property_id", propertyId)
                }
            }

            val remainingPhotos = photosForProperty(propertyId)
            remainingPhotos.forEachIndexed { index, remainingPhoto ->
                client.from(PHOTOS).update(
                    PropertyPhotoOrderRow(
                        sortOrder = index,
                        isHero = index == 0
                    )
                ) {
                    filter {
                        eq("id", remainingPhoto.id)
                        eq("property_id", propertyId)
                    }
                }
            }
            client.from(PROPERTIES).update(
                PropertyHeroPhotoNullableRow(heroPhotoPath = remainingPhotos.firstOrNull()?.storagePath)
            ) {
                filter {
                    eq("id", propertyId)
                }
            }
            clearPageCache()
            Unit
        }.onFailure {
            HavnLogger.error("Properties", "Failed to delete property photo $photoId", it)
        }
    }

    override suspend fun reorderPropertyPhotos(
        agentId: String,
        propertyId: String,
        orderedPhotoIds: List<String>
    ): Result<Unit> {
        return runCatching {
            HavnLogger.info("Properties", "Reordering photos for $propertyId")
            val photos = photosForProperty(propertyId)
            val photosById = photos.associateBy { it.id }
            val orderedPhotos = orderedPhotoIds.mapNotNull { photosById[it] }
            require(orderedPhotos.size == photos.size) { "Photo order is out of date. Refresh and try again." }

            orderedPhotos.forEachIndexed { index, photo ->
                client.from(PHOTOS).update(
                    PropertyPhotoOrderRow(
                        sortOrder = index,
                        isHero = index == 0
                    )
                ) {
                    filter {
                        eq("id", photo.id)
                        eq("property_id", propertyId)
                    }
                }
            }

            client.from(PROPERTIES).update(
                PropertyHeroPhotoNullableRow(heroPhotoPath = orderedPhotos.firstOrNull()?.storagePath)
            ) {
                filter {
                    eq("id", propertyId)
                }
            }
            clearPageCache()
            Unit
        }.onFailure {
            HavnLogger.error("Properties", "Failed to reorder photos for $propertyId", it)
        }
    }

    private suspend fun photosForProperty(propertyId: String): List<PropertyPhoto> {
        return client.from(PHOTOS)
            .select {
                filter { eq("property_id", propertyId) }
                order("sort_order", Order.ASCENDING)
                order("created_at", Order.ASCENDING)
            }
            .decodeList<PropertyPhotoRow>()
            .map { it.toDomain() }
    }

    private suspend fun photosForProperties(propertyIds: List<String>): Map<String, List<PropertyPhoto>> {
        if (propertyIds.isEmpty()) return emptyMap()
        return client.from(PHOTOS)
            .select {
                filter {
                    isIn("property_id", propertyIds)
                    eq("is_hero", true)
                }
                order("property_id", Order.ASCENDING)
                order("sort_order", Order.ASCENDING)
                order("created_at", Order.ASCENDING)
            }
            .decodeList<PropertyPhotoRow>()
            .map { it.toDomain() }
            .groupBy { it.propertyId }
    }

    internal suspend fun propertyPage(
        agentId: String,
        agencyId: String?,
        offset: Int,
        limit: Int
    ): List<Property> = propertyPage(agentId, agencyId, query = "", offset = offset, limit = limit).getOrThrow()

    private suspend fun propertySearchRows(agencyId: String?, query: String, offset: Int, limit: Int, actAsAgent: Boolean = false) =
        client.postgrest.rpc(
            function = PROPERTY_SEARCH_RPC,
            parameters = SearchPageParams(
                agencyId = agencyId,
                query = query.trim().takeIf { it.isNotBlank() },
                limit = limit,
                offset = offset,
                actAsAgent = actAsAgent
            )
        )

    private suspend fun clearPageCache() {
        pageCacheMutex.withLock { pageCache.clear() }
    }

    private suspend fun statsForProperty(propertyId: String): PropertyPerformanceStats {
        return statsForProperties(listOf(propertyId))[propertyId] ?: PropertyPerformanceStats()
    }

    private suspend fun statsForProperties(propertyIds: List<String>): Map<String, PropertyPerformanceStats> {
        if (propertyIds.isEmpty()) return emptyMap()
        return client.postgrest.rpc(
            function = PROPERTY_STATS_RPC,
            parameters = PropertyStatsParams(propertyIds = propertyIds)
        )
            .decodeList<PropertyStatsRow>()
            .associate { row ->
                val conversionRate = if (row.totalVisitors == 0) {
                    0.0
                } else {
                    row.hotLeads.toDouble() / row.totalVisitors.toDouble() * 100.0
                }
                row.propertyId to PropertyPerformanceStats(
                    totalVisitors = row.totalVisitors,
                    hotLeads = row.hotLeads,
                    followUpsSent = row.followUpsSent,
                    openingCount = row.openingCount,
                    conversionRate = conversionRate
                )
            }
    }

    private companion object {
        const val PROPERTY_PAGE_SIZE = 20
        const val PROPERTY_DETAIL_FALLBACK_LIMIT = 100
        const val PAGE_CACHE_TTL_MS = 15_000L
        const val PROPERTY_SEARCH_RPC = "search_properties_scoped_for_current_user"
        const val PROPERTY_STATS_RPC = "property_stats_for_current_user"
        const val PROPERTIES = "properties"
        const val AGENTS = "agents"
        const val PHOTOS = "property_photos"
        const val OPEN_HOUSES = "open_houses"
        const val VISITORS = "visitors"
        const val FOLLOW_UPS = "follow_ups"
        const val PROPERTY_ASSIGNMENTS = "property_assignments"
        const val PROPERTY_PHOTOS_BUCKET = HavnMediaBucket.PropertyPhotos
        val PROPERTY_SUMMARY_COLUMNS = Columns.list(
            "id",
            "agent_id",
            "owner_agent_id",
            "agency_id",
            "assigned_agent_id",
            "title",
            "address_line_1",
            "address_line_2",
            "city",
            "region",
            "postal_code",
            "country",
            "asking_price",
            "bedrooms",
            "bathrooms",
            "interior_sqft",
            "status",
            "description",
            "owner_contact_id",
            "owner_first_name",
            "owner_last_name",
            "owner_email",
            "owner_phone",
            "hero_photo_path",
            "created_at",
            "updated_at"
        )
    }
}

private fun List<Property>.sortedByLastModified(): List<Property> =
    sortedWith(
        compareByDescending<Property> { it.updatedAt ?: it.createdAt ?: "" }
            .thenByDescending { it.createdAt ?: "" }
            .thenBy { it.id }
    )

private class PropertyPagingSource(
    private val repository: SupabasePropertyRepository,
    private val agentId: String,
    private val agencyId: String?
) : PagingSource<Int, Property>() {
    override suspend fun load(params: LoadParams<Int>): LoadResult<Int, Property> {
        val offset = params.key ?: 0
        return try {
            val properties = repository.propertyPage(
                agentId = agentId,
                agencyId = agencyId,
                offset = offset,
                limit = params.loadSize
            )
            LoadResult.Page(
                data = properties,
                prevKey = (offset - params.loadSize).takeIf { it >= 0 },
                nextKey = (offset + properties.size).takeIf { properties.size == params.loadSize }
            )
        } catch (error: Throwable) {
            HavnLogger.error("Properties", "Failed to load paged property rows", error)
            LoadResult.Error(error)
        }
    }

    override fun getRefreshKey(state: PagingState<Int, Property>): Int? {
        return state.anchorPosition?.let { anchor ->
            val page = state.closestPageToPosition(anchor)
            page?.prevKey?.plus(state.config.pageSize) ?: page?.nextKey?.minus(state.config.pageSize)
        }
    }
}

@Serializable
private data class AgentIdRow(val id: String)

@Serializable
private data class AgentInsertRow(
    val id: String,
    @SerialName("full_name") val fullName: String,
    val email: String
)

@Serializable
private data class PropertyRow(
    val id: String,
    @SerialName("agent_id") val agentId: String,
    @SerialName("owner_agent_id") val ownerAgentId: String? = null,
    @SerialName("agency_id") val agencyId: String? = null,
    @SerialName("assigned_agent_id") val assignedAgentId: String? = null,
    val title: String,
    @SerialName("address_line_1") val addressLine1: String,
    @SerialName("address_line_2") val addressLine2: String? = null,
    val city: String,
    val region: String,
    @SerialName("postal_code") val postalCode: String,
    val country: String = "US",
    val latitude: Double? = null,
    val longitude: Double? = null,
    @SerialName("asking_price") val askingPrice: Long,
    val bedrooms: Double? = null,
    val bathrooms: Double? = null,
    @SerialName("interior_sqft") val interiorSqft: Int? = null,
    val status: String,
    val description: String? = null,
    @SerialName("owner_contact_id") val ownerContactId: String? = null,
    @SerialName("owner_first_name") val ownerFirstName: String? = null,
    @SerialName("owner_last_name") val ownerLastName: String? = null,
    @SerialName("owner_email") val ownerEmail: String? = null,
    @SerialName("owner_phone") val ownerPhone: String? = null,
    @SerialName("hero_photo_path") val heroPhotoPath: String? = null,
    @SerialName("hero_photo_id") val heroPhotoId: String? = null,
    @SerialName("hero_photo_storage_path") val heroPhotoStoragePath: String? = null,
    @SerialName("hero_photo_alt_text") val heroPhotoAltText: String? = null,
    @SerialName("hero_photo_sort_order") val heroPhotoSortOrder: Int? = null,
    @SerialName("hero_photo_is_hero") val heroPhotoIsHero: Boolean? = null,
    @SerialName("hero_photo_thumbnail_url") val heroPhotoThumbnailDataUrl: String? = null,
    @SerialName("hero_photo_thumbnail_width") val heroPhotoThumbnailWidth: Int? = null,
    @SerialName("hero_photo_thumbnail_height") val heroPhotoThumbnailHeight: Int? = null,
    @SerialName("hero_photo_content_type") val heroPhotoContentType: String? = null,
    @SerialName("hero_photo_size_bytes") val heroPhotoSizeBytes: Long? = null,
    @SerialName("hero_photo_created_at") val heroPhotoCreatedAt: String? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null
) {
    fun toDomain(
        photos: List<PropertyPhoto> = listOfNotNull(heroPhoto()),
        stats: PropertyPerformanceStats = PropertyPerformanceStats()
    ): Property = Property(
        id = id,
        agentId = agentId,
        ownerAgentId = ownerAgentId,
        agencyId = agencyId,
        assignedAgentId = assignedAgentId,
        title = title,
        addressLine1 = addressLine1,
        addressLine2 = addressLine2,
        city = city,
        region = region,
        postalCode = postalCode,
        country = country,
        latitude = latitude,
        longitude = longitude,
        askingPrice = askingPrice,
        bedrooms = bedrooms,
        bathrooms = bathrooms,
        interiorSqft = interiorSqft,
        status = PropertyStatus.fromDatabase(status),
        description = description,
        ownerContactId = ownerContactId,
        ownerFirstName = ownerFirstName,
        ownerLastName = ownerLastName,
        ownerEmail = ownerEmail,
        ownerPhone = ownerPhone,
        heroPhotoPath = heroPhotoPath,
        createdAt = createdAt,
        updatedAt = updatedAt,
        photos = photos,
        stats = stats
    )

    private fun heroPhoto(): PropertyPhoto? {
        val id = heroPhotoId ?: return null
        val storagePath = heroPhotoStoragePath ?: heroPhotoPath ?: return null
        return PropertyPhoto(
            id = id,
            propertyId = this.id,
            storagePath = storagePath,
            altText = heroPhotoAltText,
            sortOrder = heroPhotoSortOrder ?: 0,
            isHero = heroPhotoIsHero ?: true,
            thumbnailDataUrl = havnThumbnailOnlyPhotoSource(heroPhotoThumbnailDataUrl, heroPhotoThumbnailWidth),
            listThumbnailDataUrl = havnThumbnailOnlyPhotoSource(heroPhotoThumbnailDataUrl, heroPhotoThumbnailWidth),
            thumbnailWidth = heroPhotoThumbnailWidth,
            thumbnailHeight = heroPhotoThumbnailHeight,
            contentType = heroPhotoContentType,
            sizeBytes = heroPhotoSizeBytes,
            createdAt = heroPhotoCreatedAt
        )
    }
}

@Serializable
private data class PropertyInsertRow(
    @SerialName("agent_id") val agentId: String,
    @SerialName("owner_agent_id") val ownerAgentId: String,
    @SerialName("agency_id") val agencyId: String? = null,
    @SerialName("assigned_agent_id") val assignedAgentId: String? = null,
    val title: String,
    @SerialName("address_line_1") val addressLine1: String,
    @SerialName("address_line_2") val addressLine2: String? = null,
    val city: String,
    val region: String,
    @SerialName("postal_code") val postalCode: String,
    val country: String,
    val latitude: Double? = null,
    val longitude: Double? = null,
    @SerialName("asking_price") val askingPrice: Long,
    val bedrooms: Double? = null,
    val bathrooms: Double? = null,
    @SerialName("interior_sqft") val interiorSqft: Int? = null,
    val status: String,
    val description: String? = null,
    @SerialName("owner_contact_id") val ownerContactId: String? = null,
    @SerialName("owner_first_name") val ownerFirstName: String? = null,
    @SerialName("owner_last_name") val ownerLastName: String? = null,
    @SerialName("owner_email") val ownerEmail: String? = null,
    @SerialName("owner_phone") val ownerPhone: String? = null
)

@Serializable
private data class PropertyAssignmentRow(
    @SerialName("agent_id") val agentId: String
)

@Serializable
private data class AssignPropertyAccessParams(
    @SerialName("p_property_id") val propertyId: String,
    @SerialName("p_agent_ids") val agentIds: List<String>
)

@Serializable
private data class PropertyUpdateRow(
    val title: String,
    @SerialName("address_line_1") val addressLine1: String,
    @SerialName("address_line_2") val addressLine2: String? = null,
    val city: String,
    val region: String,
    @SerialName("postal_code") val postalCode: String,
    val country: String,
    val latitude: Double? = null,
    val longitude: Double? = null,
    @SerialName("asking_price") val askingPrice: Long,
    val bedrooms: Double? = null,
    val bathrooms: Double? = null,
    @SerialName("interior_sqft") val interiorSqft: Int? = null,
    val status: String,
    val description: String? = null,
    @SerialName("owner_contact_id") val ownerContactId: String? = null,
    @SerialName("owner_first_name") val ownerFirstName: String? = null,
    @SerialName("owner_last_name") val ownerLastName: String? = null,
    @SerialName("owner_email") val ownerEmail: String? = null,
    @SerialName("owner_phone") val ownerPhone: String? = null
)

@Serializable
private data class PropertyArchiveRow(val status: String)

@Serializable
private data class PropertyHeroPhotoRow(
    @SerialName("hero_photo_path") val heroPhotoPath: String
)

@Serializable
private data class PropertyHeroPhotoNullableRow(
    @SerialName("hero_photo_path") val heroPhotoPath: String?
)

@Serializable
private data class PropertyPhotoRow(
    val id: String,
    @SerialName("property_id") val propertyId: String,
    @SerialName("storage_path") val storagePath: String,
    @SerialName("alt_text") val altText: String? = null,
    @SerialName("sort_order") val sortOrder: Int = 0,
    @SerialName("is_hero") val isHero: Boolean = false,
    @SerialName("thumbnail_url") val thumbnailDataUrl: String? = null,
    @SerialName("thumbnail_storage_path") val thumbnailStoragePath: String? = null,
    @SerialName("list_thumbnail_url") val listThumbnailDataUrl: String? = null,
    @SerialName("list_thumbnail_storage_path") val listThumbnailStoragePath: String? = null,
    @SerialName("thumbnail_width") val thumbnailWidth: Int? = null,
    @SerialName("thumbnail_height") val thumbnailHeight: Int? = null,
    @SerialName("content_type") val contentType: String? = null,
    @SerialName("size_bytes") val sizeBytes: Long? = null,
    @SerialName("created_at") val createdAt: String? = null
) {
    fun toDomain(): PropertyPhoto = PropertyPhoto(
        id = id,
        propertyId = propertyId,
        storagePath = storagePath,
        altText = altText,
        sortOrder = sortOrder,
        isHero = isHero,
        thumbnailDataUrl = havnDisplayPhotoSource(storagePath, thumbnailDataUrl, thumbnailWidth),
        listThumbnailDataUrl = havnThumbnailOnlyPhotoSource(
            listThumbnailStoragePath ?: listThumbnailDataUrl ?: thumbnailDataUrl,
            thumbnailWidth
        ),
        thumbnailWidth = thumbnailWidth,
        thumbnailHeight = thumbnailHeight,
        contentType = contentType,
        sizeBytes = sizeBytes,
        createdAt = createdAt
    )
}

@Serializable
private data class PropertyPhotoInsertRow(
    @SerialName("property_id") val propertyId: String,
    @SerialName("uploaded_by_agent_id") val uploadedByAgentId: String,
    @SerialName("storage_path") val storagePath: String,
    @SerialName("alt_text") val altText: String? = null,
    @SerialName("sort_order") val sortOrder: Int = 0,
    @SerialName("is_hero") val isHero: Boolean = false,
    @SerialName("thumbnail_url") val thumbnailDataUrl: String? = null,
    @SerialName("thumbnail_storage_path") val thumbnailStoragePath: String? = null,
    @SerialName("list_thumbnail_url") val listThumbnailDataUrl: String? = null,
    @SerialName("list_thumbnail_storage_path") val listThumbnailStoragePath: String? = null,
    @SerialName("thumbnail_width") val thumbnailWidth: Int? = null,
    @SerialName("thumbnail_height") val thumbnailHeight: Int? = null,
    @SerialName("content_type") val contentType: String,
    @SerialName("size_bytes") val sizeBytes: Long
)

@Serializable
private data class PropertyPhotoOrderRow(
    @SerialName("sort_order") val sortOrder: Int,
    @SerialName("is_hero") val isHero: Boolean
)

@Serializable
private data class PropertyPhotoDeleteRow(
    val id: String,
    @SerialName("property_id") val propertyId: String,
    @SerialName("storage_path") val storagePath: String,
    @SerialName("thumbnail_storage_path") val thumbnailStoragePath: String? = null,
    @SerialName("list_thumbnail_storage_path") val listThumbnailStoragePath: String? = null,
    @SerialName("is_hero") val isHero: Boolean = false
)

@Serializable
private data class OpenHouseIdRow(val id: String)

@Serializable
private data class OpenHouseMetricRow(
    val id: String,
    @SerialName("property_id") val propertyId: String
)

@Serializable
private data class VisitorMetricRow(
    val id: String,
    @SerialName("open_house_id") val openHouseId: String? = null,
    val temperature: String
)

@Serializable
private data class FollowUpMetricRow(
    val id: String,
    @SerialName("visitor_id") val visitorId: String? = null,
    val status: String
)

@Serializable
private data class SearchPageParams(
    @SerialName("p_agency_id") val agencyId: String?,
    @SerialName("p_query") val query: String?,
    @SerialName("p_limit") val limit: Int,
    @SerialName("p_offset") val offset: Int,
    @SerialName("p_act_as_agent") val actAsAgent: Boolean = false
)

private data class SearchPageCacheKey(
    val agentId: String,
    val agencyId: String?,
    val actAsAgent: Boolean,
    val query: String,
    val offset: Int,
    val limit: Int
)

private data class PropertyPageCacheEntry(
    val savedAtMs: Long,
    val value: List<Property>
)

@Serializable
private data class PropertyStatsParams(
    @SerialName("p_property_ids") val propertyIds: List<String>
)

@Serializable
private data class PropertyStatsRow(
    @SerialName("property_id") val propertyId: String,
    @SerialName("opening_count") val openingCount: Int = 0,
    @SerialName("total_visitors") val totalVisitors: Int = 0,
    @SerialName("hot_leads") val hotLeads: Int = 0,
    @SerialName("follow_ups_sent") val followUpsSent: Int = 0
)

private fun PropertyDraft.toInsertRow(agentId: String, agencyId: String?): PropertyInsertRow = PropertyInsertRow(
    agentId = agentId,
    ownerAgentId = agentId,
    agencyId = agencyId,
    assignedAgentId = agentId,
    title = title.trim(),
    addressLine1 = addressLine1.trim(),
    addressLine2 = addressLine2.trim().takeIf { it.isNotBlank() },
    city = city.trim(),
    region = region.trim(),
    postalCode = postalCode.trim(),
    country = country.trim(),
    latitude = latitude,
    longitude = longitude,
    askingPrice = askingPrice.coerceAtLeast(0),
    bedrooms = bedrooms,
    bathrooms = bathrooms,
    interiorSqft = interiorSqft,
    status = status.databaseValue,
    description = description.trim().takeIf { it.isNotBlank() },
    ownerContactId = ownerContactId,
    ownerFirstName = ownerFirstName.trim().takeIf { it.isNotBlank() },
    ownerLastName = ownerLastName.trim().takeIf { it.isNotBlank() },
    ownerEmail = ownerEmail.trim().takeIf { it.isNotBlank() },
    ownerPhone = ownerPhone.trim().takeIf { it.isNotBlank() }
)

private fun PropertyDraft.toUpdateRow(): PropertyUpdateRow = PropertyUpdateRow(
    title = title.trim(),
    addressLine1 = addressLine1.trim(),
    addressLine2 = addressLine2.trim().takeIf { it.isNotBlank() },
    city = city.trim(),
    region = region.trim(),
    postalCode = postalCode.trim(),
    country = country.trim(),
    latitude = latitude,
    longitude = longitude,
    askingPrice = askingPrice.coerceAtLeast(0),
    bedrooms = bedrooms,
    bathrooms = bathrooms,
    interiorSqft = interiorSqft,
    status = status.databaseValue,
    description = description.trim().takeIf { it.isNotBlank() },
    ownerContactId = ownerContactId,
    ownerFirstName = ownerFirstName.trim().takeIf { it.isNotBlank() },
    ownerLastName = ownerLastName.trim().takeIf { it.isNotBlank() },
    ownerEmail = ownerEmail.trim().takeIf { it.isNotBlank() },
    ownerPhone = ownerPhone.trim().takeIf { it.isNotBlank() }
)

private fun String.sanitizeFileName(): String {
    return lowercase()
        .replace(Regex("[^a-z0-9._-]+"), "-")
        .trim('-')
        .ifBlank { "property-photo.jpg" }
}

private fun timestampSlug(): String = kotlin.random.Random.nextInt(100_000, 999_999).toString()
